(ns minorodata.components.sql-console.sidebar
  (:require ["copy-to-clipboard" :as copy]
            ["lucide-react" :refer [ChevronLeft ChevronRight Database
                                    ExternalLink Heart HeartOff History
                                    LayoutList Pencil RefreshCcw
                                    FileClock FileHeart]]
            ["date-fns" :refer [formatISO]]
            ["react-resize-detector" :refer [useResizeDetector]]
            [applied-science.js-interop :as js-interop]
            [goog.functions :refer [throttle]]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.components.dynamic-docs.sidebar :as dd.sidebar]
            [minorodata.components.infinite-scroll.core :as c.infinite-scroll]
            [minorodata.components.spinner.core :as c.spinner]
            [minorodata.components.sql-console.utils :refer [fetch-params
                                                             generate-columns
                                                             generate-drop-table
                                                             generate-select
                                                             generate-table-name
                                                             get-header-class
                                                             get-offset
                                                             node-object
                                                             node-title
                                                             node-type
                                                             type-icon]]
            [minorodata.components.tables.details :as c.t-details]
            [minorodata.sql-console.api]
            [minorodata.sql-console.model :refer [->sql-editor]]
            [minorodata.app.utils :as app.utils]
            [re-posh.core :as re-posh]
            [react :as react]
            [reagent.core :as reagent]
            [ui.date-time]
            [ui.button :refer [button]]
            [ui.context-menu :refer [context-menu-content context-menu-item
                                     context-menu-root context-menu-separator
                                     context-menu-trigger]]
            [ui.shortcut]
            [ui.tabs]
            [ui.tooltip :refer [tooltip]]
            [ui.tree :as ui.tree]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))

(def list-scroll-position-cache (reagent/atom {}))

(defn set-editor-content [code]
  (dispatch [:sql-console.events/set-editor-content code]))

(defn preview-select-handler [{:keys [on-close node children]}]
  (fn []
    (let [node      (js->clj node :keywordize-keys true)
          children  (js->clj children :keywordize-keys true)
          table-sql (generate-select
                     {:cols  (generate-columns children)
                      :table (generate-table-name node)})

          current-tab-idx  (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
          tdc              @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
          tab-data         (nth tdc current-tab-idx {})

          code             (:code tab-data)
          new-code         (str code "\n" table-sql)]

      (set-editor-content new-code)
      (when on-close (on-close)))))

(defn drop-table-handler [{:keys [on-close node]}]
  (fn []
    (let [node      (js->clj node :keywordize-keys true)
          table-sql (generate-drop-table
                     {:table (generate-table-name node)})

          current-tab-idx  (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
          tdc              @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
          tab-data         (nth tdc current-tab-idx {})

          code             (:code tab-data)

          new-code (str code "\n\n" table-sql)]

      (set-editor-content new-code)
      (when on-close (on-close)))))

(defn copy-drop-table-handler [{:keys [on-close node]}]
  (fn []
    (let [node      (js->clj node :keywordize-keys true)
          table-sql (generate-drop-table
                     {:table (generate-table-name node)})]

      (copy table-sql #js {:format "text/plain"})

      (when on-close (on-close)))))

(defn catalog-name-handler [{:keys [on-close node]}]
  (fn []
    (let [node      (js->clj node :keywordize-keys true)
          table-cat (:table-cat node)]

      (copy table-cat #js {:format "text/plain"})
      (when on-close (on-close)))))

(defn schema-name-handler [{:keys [on-close node]}]
  (fn []
    (let [node         (js->clj node :keywordize-keys true)
          table-schema (:table-schem node)]

      (copy table-schema #js {:format "text/plain"})
      (when on-close (on-close)))))

(defn qualified-table-name-handler [{:keys [on-close node]}]
  (fn []
    (let [node       (js->clj node :keywordize-keys true)
          qualified-table-name (generate-table-name node)]

      (copy qualified-table-name #js {:format "text/plain"})
      (when on-close (on-close)))))

(defn table-name-handler [{:keys [on-close node]}]
  (fn []
    (let [node       (js->clj node :keywordize-keys true)
          table-name (:table-name node)]

      (copy table-name #js {:format "text/plain"})
      (when on-close (on-close)))))

(defn column-name-handler [{:keys [on-close node]}]
  (fn []
    (let [node        (js->clj node :keywordize-keys true)
          column-name (:column-name node)]

      (copy column-name #js {:format "text/plain"})
      (when on-close (on-close)))))

(defn preview-data-handler [{:keys [on-close node]}]
  (fn []
    (let [node      (js->clj node :keywordize-keys true)
          table-sql (generate-select
                     {:cols  "*"
                      :table (generate-table-name node)
                      :limit 50})]

      (dispatch [:sql-console.api/post-query {:query table-sql}])
      (when on-close (on-close)))))

(defn title-sorting-fn [title-key]
  (fn [a-node b-node]
    (let [a-title (js-interop/get a-node title-key)
          b-title (js-interop/get b-node title-key)]
      (compare a-title b-title))))

(defn max-if-sql-max-varchar-size
  "Print \"MAX\" if length is 65535 (redshift limit) or 16777216 (snoflake limit). Otherwise print length."
  [len]
  (if (or (= 65535 len)
          (= 16777216 len))
    "MAX"
    (str len)))

(defn column-type-name [node]
  (let [type-name (js-interop/get node :type-name)
        char-octet-length (js-interop/get node :char-octet-length)]
    (if (and (= "VARCHAR" type-name)
             (some? char-octet-length))
      (str type-name " (" (max-if-sql-max-varchar-size char-octet-length) ")")
      type-name)))

(defn loading-tree []
  [:div {:class "p-2 flex flex-col gap-3 animate-kleene-pulse *:bg-kleene-neutral-04 dark:*:bg-kleene-dark-04 *:rounded-xs *:h-4"}
   [:div]
   [:div]
   [:div]
   [:div]
   [:div]])

(defn refresh-database-structure [nested-nodes]
  (dispatch [:sql-console.events/refetch-metadata-partial {:fetch-nested (map fetch-params nested-nodes)}]))

(defn set-history-tab [tab]

  (if (= tab :favourites)
    (dispatch [:sql-console.events/reset-favourites])
    (dispatch [:sql-console.events/reset-history]))

  (dispatch [:sql-editor/update {:sql-editor/history-tab tab}]))

(defn spinner-list-item []
  [:div {:class "m-6 flex justify-center"}
   [c.spinner/inline]])

(defn fetch-more-history []
  (let [{:sql-query-history/keys [page]} @(re-posh/subscribe [:sql-query-history/pull '[:sql-query-history/page]])]
    (dispatch [:sql-console.api/fetch-history {:page (inc page)}])))

(defn fetch-more-favourites []
  (let [{:sql-query-favourites/keys [page]} @(re-posh/subscribe [:sql-query-favourites/pull '[:sql-query-favourites/page]])]
    (dispatch [:sql-console.api/fetch-favourites {:page (inc page)}])))

(defn simple-code-preview [{:keys [className code]}]
  (let [code-ref (react/useRef nil)
        sql-statement code]

    (react/useEffect
     (fn []
       (when-let [node (js-interop/get code-ref :current)]
         (let [hljs  (js-interop/get js/window :hljs)]
           (when hljs
             (js-interop/call hljs :highlightElement node))))
       (fn []))
     #js [code-ref])

    [:div {:class "w-full overflow-hidden flex flex-col my-0 rounded-md border-kleene-neutral-03  dark:border-kleene-dark-03 border"}
     [:div {:class ""}
      [:pre {:class ["p-1.5 overflow-auto language-sql" className]
             :ref code-ref}
       code]]]))

(defn list-item [{:keys [id]}]
  (let [query @(re-posh/subscribe [:sql-query/pull id])]
    [tooltip {:trigger-as-child true
              :class "p-0 max-w-(--radix-tooltip-content-available-width) max-h-(--radix-tooltip-content-available-height) overflow-auto"
              :content-props {:side "right"}
              :content [:f> simple-code-preview {:code (str (:sql-query/command query))}]}

     [:div {:data-cy  "query-history-snippet"
            :class    ["items-start p-2 hover:bg-kleene-neutral-05 focus:bg-kleene-neutral-04 dark:text-kleene-neutral-01 dark:hover:bg-kleene-dark-02"
                       "border-y border-t-kleene-neutral-01 dark:border-t-kleene-dark-02 border-b-kleene-neutral-01 dark:border-b-kleene-dark-02"
                       "group relative"]}

      [:div {:class "flex flex-row gap-1 pb-1 group-hover:opacity-5"}

       [:> (if (:sql-query/favourite query)
             FileHeart
             FileClock)
        {:size 16 :class "text-kleene-icon opacity-90 shrink-0"}]

       [:div {:class   "cursor-pointer whitespace-nowrap grow shrink overflow-hidden text-ellipsis text-xs text-left text-kleene-text"
              :data-cy "query-history-snippet-title"
              :title "Click to copy into editor"
              :on-click #(set-editor-content (:sql-query/command query))}
        (or (:sql-query/name query)
            (:sql-query/command query))]]

      [:div {:class ["flex flex-row collapse group-hover:visible"
                     "absolute top-0 left-0 right-0"
                     "px-2 py-1"]}
       [:div {:class "flex items-center group grow"}
        [:div {:class "grow-0 shrink-0 flex gap-2 text-xs leading-zero"}
         [button {:size     :icon-sm
                  :variant  :empty
                  :icon     (if (:sql-query/favourite query)
                              HeartOff
                              Heart)
                  :on-click (fn [evt]
                              (js-interop/call evt :stopPropagation)
                              (dispatch [:sql-console.api/favourite-query
                                         (:db/id query)
                                         (:sql-query/sql-query-id query)
                                         (not (:sql-query/favourite query))]))
                  :title    (if (:sql-query/favourite query)
                              "Remove from favourites"
                              "Add to favourites")
                  :class    "bg-transparent"
                  :data-cy  "query-history-snippet-fav"
                  :icon-props {:fontSize "small"}}]

         [button {:size     :icon-sm
                  :variant  :empty
                  :icon     ExternalLink
                  :icon-props {:fontSize "small"}
                  :on-click (fn [evt]
                              (js-interop/call evt :stopPropagation)
                              (set-editor-content (:sql-query/command query)))
                  :class   "hidden group-hover:inline-flex bg-transparent"
                  :title   "Copy into current editor"
                  :data-cy "query-history-snippet-open"}]

         [button {:size     :icon-sm
                  :variant  :empty
                  :icon     Pencil
                  :icon-props {:fontSize "small"}
                  :on-click (fn [evt]
                              (js-interop/call evt :stopPropagation)
                              (dispatch [:sql-editor/update
                                         {:sql-editor/sql-query-to-edit (:sql-query/sql-query-id query)}]))
                  :class   "hidden group-hover:inline-flex bg-transparent"
                  :title   "Edit history entry"
                  :data-cy "query-history-snippet-edit"}]]]

       [:div {:class "text-kleene-dark-05 dark:text-kleene-neutral-01 opacity-80 text-[10px] pt-1.5"}
        [ui.date-time/date-tooltip {:date (:sql-query/created-at query)}
         (app.utils/format-date (:sql-query/created-at query))]]]]]))

(def group-by-date
  (partial group-by (fn [query]
                      (let [created-at (:sql-query/created-at query)
                            in-local-tz (js/Date. created-at)
                            date (formatISO in-local-tz)]
                        (re-find #"\d+-\d+-\d+" date)))))

(defn generic-history-list [{:keys [queries-sub loading-id empty-message fetch-more]}]
  (let [queries  @(re-posh/subscribe [queries-sub])
        grouped-queries (reverse (sort-by first (group-by-date queries)))
        loading? @(re-posh/subscribe [:mold-http/loading? loading-id])]
    (if (and (= (count queries) 0)
             (not loading?))

      [:div {:class "p-4"}
       [:h3 empty-message]]

      [:<>
       (into
        [c.infinite-scroll/main
         {:component       "div"
          :loader          [spinner-list-item]
          :loading?        loading?
          :on-scroll-end   fetch-more}]
        (for [[date queries-list] grouped-queries]
          (into
           [:div {:class "relative"}
            [:p {:class ["sticky top-0 z-10"
                         "border-y border-t-kleene-neutral-01 dark:border-t-kleene-dark-02 border-b-kleene-neutral-01 dark:border-b-kleene-dark-02"
                         "px-3 py-1.5 text-sm font-semibold leading-6 dark:bg-black bg-white"]}
             (app.utils/format-date date {:today "'Today'"
                                          :yesterday "'Yesterday'"
                                          :date "d MMM yy"})]]
           (for [query queries-list]
             (let [id (:db/id query)]
               ^{:key id}
               [list-item {:id id}])))))])))

(defn favourites-list []
  [generic-history-list {:queries-sub :sql-console.subs/ordered-sql-query-favourites
                         :loading-id :sql-console.api/fetch-favourites
                         :empty-message "No favourites"
                         :fetch-more fetch-more-favourites}])

(defn history-list []
  [generic-history-list {:queries-sub :sql-console.subs/ordered-sql-query-history
                         :loading-id :sql-console.api/fetch-history
                         :empty-message "No queries in history"
                         :fetch-more fetch-more-history}])

(defn node-column-type-name [node]
  (let [type-name (:type-name node)
        char-octet-length (:char-octet-length node)]
    (if (and (= "VARCHAR" type-name)
             (some? char-octet-length))
      (str type-name " (" (max-if-sql-max-varchar-size char-octet-length) ")")
      type-name)))

(defn node-description [node children-count]
  (if (:column-name node)
    (node-column-type-name node)
    (when children-count
      (str children-count " " (node-object node)))))

(defn generate-select-from-metadata-handler [{:keys [node fetch-data]}]
  (let [params   (fetch-params node)
        children @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params params]
                                      :sql-metadata-partial/metadata])
        generate #((preview-select-handler {:node     node
                                            :children %}))]
    (if children
      #(generate children)
      #(fetch-data params {:on-success generate}))))

(defn copy-select-from-metadata-handler [{:keys [node fetch-data on-close]}]
  (let [params   (fetch-params node)
        children @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params params]
                                      :sql-metadata-partial/metadata])

        generate (fn [children]
                   (let [table-sql (generate-select
                                    {:cols  (generate-columns children)
                                     :table (generate-table-name node)})]

                     (copy table-sql #js {:format "text/plain"})
                     (when on-close (on-close))))]

    (if children
      #(generate children)
      #(fetch-data params {:on-success generate}))))

(defn open-table-info-modal [{:keys [node]}]
  (fn []
    (let [{:keys [catalog schema table]} (fetch-params node)]
      (c.t-details/open-table-info-modal {:db catalog :schema schema :table table}))))

(defn node-context-menu [{:keys [type node fetch-data]}]
  (let [show-table-details? @(re-posh/subscribe [:app.subs/enabled-for-bigquery :tables])]
    (case type
      "catalog" [context-menu-item {:on-click (catalog-name-handler {:node node})}
                 "Copy catalog name"]
      "schema" [context-menu-item {:on-click (schema-name-handler {:node node})}
                "Copy schema name"]
      "column" [context-menu-item {:on-click (column-name-handler {:node node})}
                "Copy column name"]
      "table" [:<>
               [context-menu-item {:on-click (preview-data-handler {:node node :children [{:column-name "*"}]})}
                "Preview data"]

               [context-menu-item {:on-click (qualified-table-name-handler {:node node})}
                "Copy table name"]

               [context-menu-item {:on-click (generate-select-from-metadata-handler {:node node :fetch-data fetch-data})}
                "Generate SELECT"]

               [context-menu-item {:on-click (copy-select-from-metadata-handler {:node node :fetch-data fetch-data})}
                "Copy SELECT"]

               (when show-table-details?
                 [context-menu-item {:on-click (open-table-info-modal {:node node})}
                  "Open table info"])

               [context-menu-separator]

               [context-menu-item {:on-click (drop-table-handler {:node node})}
                "Generate DROP TABLE statement"]

               [context-menu-item {:on-click (copy-drop-table-handler {:node node})}
                "Copy DROP TABLE statement"]]

      nil)))

(defonce tree-node-open-cache #js {})

(defn ->node-cache-key [node]
  (str (:table-cat node) "." (:table-schem node) "." (:table-name node) "." (:column-name node)))

(defn tree-node [{:keys [node on-open]}]
  (let [type             (node-type node)
        title            (node-title node)
        leaf             (= type "column")
        params           (fetch-params node)
        children         @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params params]
                                              :sql-metadata-partial/metadata])
        loader-key       (minorodata.sql-console.api/->metadata-request-id params)
        loading?         @(re-posh/subscribe [:mold-http/loading-id? :sql-console.api/fetch-metadata loader-key])
        children-count   (some-> children count)
        cache-key        (->node-cache-key node)
        [open on-open-change] (ui.tree/use-cached-open-state tree-node-open-cache cache-key node)]

    [ui.tree/tree-node
     {:open           open
      :on-open-change (fn [v]
                        (when (and v params)
                          (on-open params))
                        (on-open-change v))
      :class          (get-offset type)}
     [context-menu-root
      [context-menu-trigger
       [ui.tree/tree-row-trigger
        {:leaf      leaf
         :row-class (clsx "sticky transition-colors" (get-header-class type))}
        [ui.tree/tree-row-icon (type-icon type)]
        [ui.tree/tree-row-label {:title title} title]
        (when-let [secondary (node-description node children-count)]
          [ui.tree/tree-row-secondary-label secondary])]]
      [context-menu-content
       [node-context-menu {:type       type
                           :node       node
                           :fetch-data on-open}]]]
     [ui.tree/tree-node-content
      (when loading?
        [:span {:class "ml-8 py-1 flex flex-row gap-2"}
         [c.spinner/inline-configurable {:size 14}]
         [typography/label {:class "whitespace-nowrap text-[10px] text-kleene-dark-05 dark:text-kleene-neutral-01 mt-0.5"}
          "Loading..."]])
      (into [:div {:class "flex flex-col"}]
            (for [s children]
              ^{:key (->node-cache-key s)}
              [:f> tree-node {:on-open on-open
                              :params  params
                              :node    s}]))]]))

(defn tree-view [{:keys [children]}]
  (let [fetch-catalog-tables (fn [params & [callbacks]]
                               (dispatch [:sql-console.api/fetch-metadata params callbacks]))]
    [:div {:class "flex h-full min-h-0 flex-col"}
     [:div {:class "min-h-0 flex-1 overflow-y-auto"}
      (into
       [:div {:class "p-2"}]
       (for [i (range (count children))]
         [:f> tree-node {:on-open  fetch-catalog-tables
                         :node     (nth children i)}]))]

     [:div {:class "shrink-0 bg-kleene-background flex items-center justify-between border-t border-t-kleene-neutral-01 dark:border-t-kleene-dark-02"}
      [tooltip {:trigger-as-child false
                :content "Reload database tree"}
       [button {:size     :small
                :variant  :empty-transparent
                :data-cy  :refresh-database-structure
                :title    "Refresh database structure"
                :on-click #(refresh-database-structure (filter map? (js/Object.values tree-node-open-cache)))
                :class    "transition-opacity"
                :icon     RefreshCcw
                :component :div}
        "Refresh DB"]]]]))

(defn history-tabs []
  {"database-tree" {:title "Database"
                    :icon  Database}
   "history"       {:title   "History"
                    :icon    History
                    :event   #(set-history-tab :history)
                    :data-cy "sql-console-history-tab"}
   "favourites"    {:title   "Favourites"
                    :icon    Heart
                    :event   #(set-history-tab :favourites)
                    :data-cy "sql-console-favourites-tab"}
   "transforms"    {:title   "Transforms"
                    :icon    LayoutList
                    :active-icon-class "text-black/50 bg-[#D9C4FA] p-0.5 -my-0.5 rounded-xs"
                    :event   #(set-history-tab :transforms)
                    :data-cy "sql-console-transforms-tab"}})

(defn metadata-tree []
  (let [catalogs    @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params {:object "catalogs"}]
                                         :sql-metadata-partial/metadata])
        loading?    (and (not catalogs) @(re-posh/subscribe [:mold-http/loading? :sql-console.api/fetch-metadata]))]
    (if loading?
      [loading-tree]
      [tree-view {:children catalogs}])))

(defn open-group-transforms [group-id]
  (let [open-all (fn [transforms]
                   (dispatch [:sql-console-modal.events/open-transforms (map :id transforms)]))]

    (dispatch [:transform.api/fetch-transforms {:group-id       group-id
                                                :items-per-page 1000
                                                :on-success     open-all}])))

(defonce group-open-cache (reagent/atom {}))

(defn group-list-item [{:keys [group-id selected marked on-select]}]
  (let [{:keys [name]} @(re-posh/subscribe [:group.subs/panel-group group-id])
        [expanded? set-expanded] (react/useState false)]

    (react/useEffect
     (fn []

       (let [restored-state (get @group-open-cache group-id false)]
         (set-expanded restored-state))

       (fn []))
     #js [])

    [:div
     [ui.context-menu/context-menu-root
      [ui.context-menu/context-menu-trigger
       [dd.sidebar/group-list-button {:component :div
                                      :expanded  expanded?
                                      :mark      (when marked
                                                   [:div {:class "h-3 w-3 bg-[#D9C4FA] rounded-full"}])
                                      :on-click  (fn []
                                                   (let [new-state (not expanded?)]
                                                     (swap! group-open-cache assoc group-id new-state)
                                                     (set-expanded new-state)))}
        name]]

      [ui.context-menu/context-menu-content
       [ui.context-menu/context-menu-item
        {:on-click #(open-group-transforms group-id)}
        "Open all"]]]

     (when expanded?
       [dd.sidebar/transforms-list {:group-id  group-id
                                    :selected  selected
                                    :on-select on-select}])]))

(defn transforms-list []
  (let [tdc      @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        groups   @(re-posh/subscribe [:group.subs/ordered-groups])
        loading? @(re-posh/subscribe [:mold-http/loading? :group.api/fetch-groups])

        selected (->> tdc
                      (keep #(get-in % [:metadata :master-id]))
                      set)

        on-select (fn [id]
                    (dispatch-sync [:sql-console-modal.events/open-transform id {:preview true}]))]

    (react/useEffect
     (fn []

       (dispatch [:groups-panel/add nil])
       (dispatch [:group.api/fetch-groups {:replace? true
                                           :items-per-page 1000}])

       (fn []))
     #js [])

    [dd.sidebar/groups-with-search {:on-select on-select
                                    :only-transforms true}
     [:div {:class "flex flex-col"}
      (for [{:group/keys [id]} groups]
        ^{:key id}
        [:f> group-list-item
         {:group-id  id
          :selected  selected
          :loading?  loading?
          :marked    (some #(= (get-in % [:metadata :group-id]) id) tdc)
          :on-select on-select}])]]))

(defn sidebar [{:keys [collapsed? toggle-sidebar show-collapse-shortcut?]}]
  (let [sidebar-tab (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/sidebar-tab])
                        "database-tree")
        set-tab     (fn [tab-id]
                      (let [tab-config (get (history-tabs) tab-id)]
                        (when (:event tab-config) (:event tab-config))
                        (dispatch [:sql-editor/update (->sql-editor {:sidebar-tab tab-id})])
                        (dispatch [:sql-console.events/save-config-to-local-storage])))
        scroll-handler (fn scroll-handler [evt]
                         (let [position (js-interop/get-in evt [:target :scrollTop])]
                           (when (not= -1 position)
                             (swap! list-scroll-position-cache assoc sidebar-tab position))))

        on-scroll (throttle scroll-handler 10)

        scrollable-portion-ref (react/useRef nil)

        resize-handler (react/useCallback
                        (fn [props]
                          (let [{:keys [width]} (js->clj props :keywordize-keys true)]
                            (dispatch [:sql-editor/update (->sql-editor {:sidebar-width width})])
                            (dispatch [:sql-console.events/save-config-to-local-storage])))
                        #js [])

        {:keys [ref]} (js->clj (useResizeDetector #js {:handleWidth  true
                                                       :handleHeight true
                                                       :refreshMode  "throttle"
                                                       :refreshRate  100
                                                       :onResize     resize-handler})
                               :keywordize-keys true)]

    (react/useEffect
     (fn []

       (let [saved-scroll-position (get @list-scroll-position-cache sidebar-tab 0)
             scrollable-element (js-interop/get scrollable-portion-ref :current)]

         (when (and scrollable-element
                    saved-scroll-position)
           (js/setTimeout
            (fn []
              (js-interop/assoc! scrollable-element :scrollTop saved-scroll-position))
            10)))

       (fn []))
     #js [sidebar-tab])

    [:div {:class (clsx "overflow-hidden flex flex-col h-full"
                        {"transition-transform" collapsed?})
           :ref ref}
     [:header {:class   ["flex items-center justify-between border-b border-b-kleene-neutral-01 dark:border-b-kleene-dark-02"
                         (when collapsed? "flex-col")]
               :data-cy :panel-title}
      (when-not collapsed?
        [:div {:class "-mb-px"}
         [ui.tabs/tabs {:value           sidebar-tab
                        :on-value-change set-tab
                        :size            :small
                        :variant         :underlined
                        :tabs            (mapv (fn [[tab-id {:keys [title icon active-icon-class]}]]
                                                 {:value tab-id
                                                  :data-cy (str "sql-console-tab-" tab-id)
                                                  :label [:div {:class "mx-auto flex gap-2 items-center leading-5"}
                                                          [:div {:class (clsx "leading-zero" {active-icon-class (= sidebar-tab tab-id)})}
                                                           [:> icon {:size 20}]]
                                                          (when (= tab-id sidebar-tab) [:div title])]})
                                               (history-tabs))}]])

      [:div {:class ["flex gap-2"
                     (when-not collapsed?) "mr-1"
                     (when collapsed? "py-1 px-0 w-full border-b border-b-kleene-neutral-01 dark:border-b-kleene-dark-02")]}
       [tooltip {:trigger-as-child false
                 :content (if show-collapse-shortcut?
                            [ui.shortcut/tooltip-content
                             {:content "Toggle sidebar"
                              :keys ["cmd" "i"]}]
                            "Toggle sidebar")}
        [button {:size     :icon
                 :component :div
                 :variant  :empty
                 :data-cy  :toggle-menu
                 :class    (when collapsed? "ml-2")
                 :on-click toggle-sidebar
                 :icon     (if collapsed? ChevronRight ChevronLeft)}]]]

      (when collapsed?
        [:div {:class "py-2"}
         (into [:div {:class "flex flex-col gap-1"}]
               (mapv (fn [[tab-id {:keys [title icon active-icon-class]}]]

                       [button {:size     :icon
                                :component :div
                                :variant  :empty
                                :data-cy  (str "sql-console-tab-" tab-id)
                                :class    "cursor-pointer"
                                :on-click (fn []
                                            (toggle-sidebar)
                                            (set-tab tab-id))
                                :title title
                                :icon     icon}])
                     (history-tabs)))])]

     [:div {:class (clsx "transition-opacity min-h-0 flex-1 overflow-hidden"
                         {"opacity-0 pointer-events-none" collapsed?})
            :ref scrollable-portion-ref
            :on-scroll on-scroll}
      (case sidebar-tab
        "database-tree"
        [metadata-tree]

        "history"
        [history-list]

        "favourites"
        [favourites-list]

        "transforms"
        [:div {:class "h-full min-h-0 overflow-y-auto"}
         [:f> transforms-list]]

        nil)]]))
